import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { EditorState } from 'draft-js';
import {
  Modal,
  ModalBody,
  Form,
  Label,
  Button,
  FormGroup,
  CustomInput,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Alert,
  FormFeedback
} from 'reactstrap';
import { GQL_PAYMENTLINK_CREATE_BY_SLUG, GQL_PAYMENTLINK_UPDATE_BY_SLUG } from '../../../gqls';
import { sanitizeDOM, slug, formatQtyQurbanMultiply, validator } from '../../../utils';
import { updatePaymentLinks } from '../../../actions/payment-links';
import { CustomStyles } from '../../custom-styles/react-select-styles';
import { getPlainText, getContentString, getEditorContent } from '../../rich-text-editor';
import Icon from '../../icon';
import SubmitButton from '../../submit-button';
import DonationForm from './components/donation-form';
import QurbanForm from './components/qurban-form';
import WaqafForm from './components/waqaf-form';
import MayarAlert from '../../alert/alert';

const ModalFundraising = ({ isOpen = false, onClose, onSuccess, data }) => {
  if (!isOpen) return null;

  const dispatch = useDispatch();
  const stateEdit = EditorState.createEmpty();
  const { register, handleSubmit, errors, control, getValues } = useForm();
  const multipleCoverContent = useSelector(state => state.multipleCoverContent);
  const [isolatedMultipleCoverContent, setIsolatedMultipleCoverContent] = useState([]); // state for modal create product only

  const [submitError, setSubmitError] = useState(null);
  const [invalidDescription, setInvalidDescription] = useState(false);

  const [description, setDescription] = useState('');
  const [textDescription, setTextDescription] = useState('');
  const [fundraisingType, setFundraisingType] = useState('');
  const [qurbanType, setQurbanType] = useState(data?.qurbanType ? data?.qurbanType : null);
  const [date, setDate] = useState(data ? data.expiredAt : null);
  const [link, setLink] = useState(data ? data.link : '');
  const [editorState, setEditorState] = useState(stateEdit);
  const [check, setCheck] = useState(true);

  const [multipleImageId, setMultipleImageId] = useState('');
  const [deletedCoverContents, setDeletedCoverContents] = useState([]);

  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [enableAffiliate, setEnableAffiliate] = useState(Boolean(data?.affiliateCommission));

  const setEditor = content => {
    setEditorState(content);
    setDescription(getContentString(content));
    setTextDescription(getPlainText(content));
  };

  const [insertPaymentLinkBySlug, { data: newPaymentLink, loading: submitCreateLoading }] = useMutation(
    GQL_PAYMENTLINK_CREATE_BY_SLUG
  );

  const [editPaymentLinkBySlug, { loading: submitUpdateLoading }] = useMutation(GQL_PAYMENTLINK_UPDATE_BY_SLUG);

  const isUpdate = data ? true : false;
  if (!data) data = {};

  useEffect(() => {
    if (isUpdate && data.subType !== null) {
      setFundraisingType(data.subType);
    } else if (isUpdate && data.subType === null) {
      setFundraisingType('FUNDRAISING_DONATION');
    }
  }, [data]);

  useEffect(() => {
    if (textDescription.length <= 0 && !check) {
      setInvalidDescription(true);
    } else {
      setInvalidDescription(false);
    }
  }, [textDescription]);

  useEffect(() => {
    if (isUpdate) {
      const content = getEditorContent(data.description);
      setEditor(content);
    }
  }, [data]);

  const onSubmit = async values => {
    if (textDescription.length <= 0) {
      setCheck(false);
      setInvalidDescription(true);
      return;
    }

    const { isTargetVisible, name, isPaymentLinkWithdrawalVisible } = values;

    if (values.target) values.target = parseFloat(values.target);
    if (values.amount) {
      values.amount = parseFloat(values.amount);
    } else {
      values.amount = null;
    }
    if (values.crossoutPrice) values.crossoutPrice = parseFloat(values.crossoutPrice);
    else values.crossoutPrice = null;

    if (values.affiliateCommission) values.affiliateCommission = parseInt(values.affiliateCommission);
    else values.affiliateCommission = null;

    if (date !== null) setDate(date);
    if (fundraisingType !== 'FUNDRAISING_QURBAN') {
      values.fundraisingSettings = JSON.stringify({ isTargetVisible, isPaymentLinkWithdrawalVisible });
      delete values.isTargetVisible;
      delete values.isPaymentLinkWithdrawalVisible;
    }

    if (fundraisingType === 'FUNDRAISING_QURBAN') {
      if (isUpdate && !/SAPI|UNTA/.test(qurbanType)) {
        values.qty = Number(data.qty) + Number(values.qty);
      } else {
        values.qty = Number(values.qty);
      }

      if (qurbanType) {
        values.qurbanType = qurbanType;
      }

      if (isUpdate && qurbanType === 'SAPI') {
        values.qty = Number(data.qty) + formatQtyQurbanMultiply(values.qty, qurbanType);
      } else if (qurbanType === 'SAPI') {
        values.qty = formatQtyQurbanMultiply(values.qty, qurbanType);
      }

      if (isUpdate && qurbanType === 'UNTA') {
        values.qty = Number(data.qty) + formatQtyQurbanMultiply(values.qty, qurbanType);
      } else if (qurbanType === 'UNTA') {
        values.qty = formatQtyQurbanMultiply(values.qty, qurbanType);
      }
    }

    const paymentLinkObject = {
      ...values,
      ...(multipleImageId && { multipleImageId }),
      expiredAt: date,
      status: 'active',
      description: sanitizeDOM(description),
      ...(deletedCoverContents.length && {
        deletedCoverContents: deletedCoverContents.map(item => {
          if ((item.file?.mimetype || '').includes('video') || item?.videoId || item?.value) {
            return {
              videoMultipleFileId: item?.id
            };
          }
          return {
            imageId: item.editImage?.id || item.id
          };
        })
      }),
      ...(isUpdate &&
        multipleCoverContent.length && {
        orderedMultipleCoverContent: multipleCoverContent.map(item => {
          if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value) {
            return {
              position: Number(item.position),
              videoId: item.videoId || item.file?.id || item.value,
              editId: item?.id
            };
          }
          return {
            position: Number(item.position),
            imageId: item.editImage?.id || item.id
          };
        })
      }),
      ...(!isUpdate &&
        isolatedMultipleCoverContent.length && {
        orderedMultipleCoverContent: isolatedMultipleCoverContent.map(item => {
          if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value) {
            return {
              position: Number(item.position),
              videoId: item.videoId || item.file?.id || item.value,
              editId: item?.id
            };
          }
          return {
            position: Number(item.position),
            imageId: item.editImage?.id || item.id
          };
        })
      })
    };

    try {
      if (isUpdate) {
        paymentLinkObject.id = data.id;
        await editPaymentLinkBySlug({
          variables: { input: paymentLinkObject }
        });

        dispatch(updatePaymentLinks(Math.random()));
      } else {
        paymentLinkObject.type = 'fundraising';
        paymentLinkObject.link = slug(name);
        paymentLinkObject.subType = fundraisingType;

        const paymentLink = await insertPaymentLinkBySlug({
          variables: { input: paymentLinkObject }
        });

        if (paymentLink?.data?.insertPaymentLinkBySlug.errorMessage) {
          throw new Error(
            paymentLink?.data?.insertPaymentLinkBySlug.errorMessage || 'Terjadi kesalahan saat membuat produk'
          );
        }

        //segment tracking
        window.analytics.track('Create Fundraising', values);
      }
    } catch (error) {
      setSubmitError(error.message || 'Error! Silahkan coba lagi atau hubungi CS');
      window.analytics.track('Create Fundraising - ERROR', values);
    }
  };

  if (
    newPaymentLink &&
    newPaymentLink.insertPaymentLinkBySlug &&
    !newPaymentLink.insertPaymentLinkBySlug.errorMessage
  ) {
    onSuccess(newPaymentLink.insertPaymentLinkBySlug.id, fundraisingType);
  }

  const fundraisingForm = type => {
    switch (type) {
      case 'FUNDRAISING_DONATION':
        return (
          <DonationForm
            data={data}
            isUpdate={isUpdate}
            hookForm={{ register, errors, control }}
            state={{
              link,
              date,
              invalidDescription,
              editorState
            }}
            setState={{ setLink, setDate, setEditor }}
            setMultipleImageId={setMultipleImageId}
            setDisableSubmitButton={setDisableSubmitButton}
            deletedCoverContents={deletedCoverContents}
            setDeletedCoverContents={setDeletedCoverContents}
            isolatedMultipleCoverContent={!isUpdate && isolatedMultipleCoverContent} // use isolated state instead to avoid mixing value when visiting product detail page
            setIsolatedMultipleCoverContent={!isUpdate && setIsolatedMultipleCoverContent}
          />
        );
      case 'FUNDRAISING_QURBAN':
        return (
          <QurbanForm
            data={data}
            isUpdate={isUpdate}
            hookForm={{ register, errors, control, getValues }}
            state={{
              link,
              date,
              invalidDescription,
              editorState,
              qurbanType
            }}
            setState={{ setLink, setDate, setEditor, setQurbanType }}
            setMultipleImageId={setMultipleImageId}
            setDisableSubmitButton={setDisableSubmitButton}
            deletedCoverContents={deletedCoverContents}
            setDeletedCoverContents={setDeletedCoverContents}
            isolatedMultipleCoverContent={!isUpdate && isolatedMultipleCoverContent} // use isolated state instead to avoid mixing value when visiting product detail page
            setIsolatedMultipleCoverContent={!isUpdate && setIsolatedMultipleCoverContent}
          />
        );
      case 'FUNDRAISING_WAQAF':
        return (
          <WaqafForm
            data={data}
            isUpdate={isUpdate}
            hookForm={{ register, errors, control }}
            state={{
              link,
              date,
              invalidDescription,
              editorState
            }}
            setState={{ setLink, setDate, setEditor }}
            setMultipleImageId={setMultipleImageId}
            setDisableSubmitButton={setDisableSubmitButton}
            deletedCoverContents={deletedCoverContents}
            setDeletedCoverContents={setDeletedCoverContents}
            isolatedMultipleCoverContent={!isUpdate && isolatedMultipleCoverContent} // use isolated state instead to avoid mixing value when visiting product detail page
            setIsolatedMultipleCoverContent={!isUpdate && setIsolatedMultipleCoverContent}
          />
        );
      default:
        return '';
    }
  };

  const categoryOptions = [
    { label: 'Donasi', value: 'FUNDRAISING_DONATION' },
    { label: 'Qurban', value: 'FUNDRAISING_QURBAN' },
    { label: 'Wakaf', value: 'FUNDRAISING_WAQAF' }
  ];

  return (
    <Modal isOpen={isOpen} fade size="lg">
      <div className="modal-header">
        <h5 className="modal-title h2">{isUpdate ? 'Edit' : 'Buat'} Penggalangan Dana</h5>
        <Button className="close" color="" onClick={onClose}>
          <Icon name="x" />
        </Button>
      </div>
      <ModalBody>
        <Alert color="light" className="mb-20">
          Baca mengenai aturan penggalangan dana{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.notion.so/mayarid/Panduan-Menggalang-Dana-Mayar-de545450dea64b72b1974a22fc7c34b7"
          >
            disini
          </a>
          . Mayar berhak menutup sepihak penggalangan dana yang melanggar aturan.
        </Alert>
        {submitError && <MayarAlert isError message={submitError} />}

        {!isUpdate && (
          <div className="mb-20">
            <Label>Tipe Penggalangan Dana*</Label>
            <Select
              options={categoryOptions}
              styles={CustomStyles}
              placeholder="Pilih kategori..."
              onChange={({ value }) => setFundraisingType(value)}
              defaultValue={categoryOptions.find(({ value }) => value === fundraisingType)}
            />
          </div>
        )}

        <Form onSubmit={handleSubmit(onSubmit)} id="create-payment-link">
          {fundraisingForm(fundraisingType)}

          <FormGroup>
            <CustomInput
              id="affiliateSwitch"
              label="Produk bisa diaffiliate"
              defaultChecked={enableAffiliate}
              type="switch"
              color="primary"
              onChange={e => setEnableAffiliate(e.target.checked)}
            />
          </FormGroup>

          {enableAffiliate && (
            <FormGroup>
              <Label for="affiliateCommission">Komisi Affiliate</Label>
              <InputGroup>
                <Input
                  type="number"
                  name="affiliateCommission"
                  id="affiliateCommission"
                  defaultValue={data?.affiliateCommission}
                  invalid={errors.affiliateCommission}
                  innerRef={register({
                    min: validator.min(1),
                    max: validator.max(70)
                  })}
                />
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>%</InputGroupText>
                </InputGroupAddon>

                <FormFeedback>{errors.affiliateCommission?.message}</FormFeedback>
                <small>
                  Komisi secara otomatis dipotong dari persentase total harga penjualan (setelah dipotong kode diskon
                  jika ada, biaya channel dan biaya Mayar). Komisi hanya untuk affiliator yang terdaftar pada produk
                  ini.
                </small>
              </InputGroup>
            </FormGroup>
          )}
          <SubmitButton
            size="lg"
            block
            color="brand"
            type="submit"
            className="text-center"
            text={`${isUpdate ? 'Edit' : 'Buat'} Penggalangan Dana`}
            loading
            isLoading={submitCreateLoading || submitUpdateLoading}
            isDisabled={disableSubmitButton}
          />
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ModalFundraising;
